import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { observer, inject } from 'mobx-react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import ProfileOneIcon from '../svg/ProfileOneIcon';
import OrdersIcon from '../svg/OrdersIcon';
import LogoutIcon from '../svg/LogoutIcon';
import CloseOneIcon from '../svg/CloseOneIcon';
import ReorderIcon from '../svg/ReorderIcon';
import ArrowTwoIcon from '../svg/ArrowTwoIcon';
import HeartIcon from '../svg/HeartIcon';
import Link from '@/components/common/Link';
import { handleLogout } from '@/utils/auth';

const MobileMenu = inject('studioStore', 'generalSettingsStore')(observer(({ studioStore, generalSettingsStore, topMenuItems = [], mainMenuItems = [], onClose, isOpen }) => {
  const t = useTranslations('Menu');
  const accountT = useTranslations('Account');
  const router = useRouter();
  const [expandedItems, setExpandedItems] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const isTokenValid = !!token;
    setIsLoggedIn(isTokenValid);

    if (isTokenValid) {
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        setUserData(JSON.parse(storedUserData));
      } else {
        fetch('/api/user', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })
          .then(response => response.json())
          .then(data => {
            setUserData(data);
            localStorage.setItem('userData', JSON.stringify(data));
          })
          .catch(error => {
            console.error('Error fetching user data:', error);
          });
      }
    }
  }, []);

  const container = studioStore.cart.container;
  const menuBackgroundColor = studioStore?.cart?.background_color ?? 'white';
  const primaryColor = studioStore?.colors?.primary_color;

  // Check if favorites feature is enabled
  const isFavoritesEnabled = generalSettingsStore?.showFavorites;

  let classes = "mobile-menu-content";
  const style = {
    backgroundColor: menuBackgroundColor,
  };

  if (container === "stamp") {
    classes = classes + " stamp-border";
    style.backgroundImage = `radial-gradient(8px, transparent 98%, ${menuBackgroundColor}),linear-gradient(${menuBackgroundColor} 0px, ${menuBackgroundColor} 0px)`;
    style.backgroundColor = "";
    style.padding = "8px";
  }

  const handleLogoutClick = async () => {
    onClose();
    await handleLogout(router);
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId],
    );
  };

  // Organize items into parent and child structure
  const parentItems = mainMenuItems.filter(item => item.parent === "0");
  const childItems = mainMenuItems.filter(item => item.parent !== "0");

  const getChildItems = (parentId) => {
    return childItems.filter(item => item.parent === String(parentId));
  };

  const renderMenuItem = (item, level = 0) => {
    const subItems = getChildItems(item.id);
    const hasChildren = subItems.length > 0;
    const isExpanded = expandedItems.includes(String(item.id));

    return (
      <div key={item.id} className="mb-0">
        <div className={`flex items-center justify-between ${level > 0 ? 'pl-4' : ''}`}>
          <Link
            href={item.url}
            className={`block px-3 text-primary-studio font-secondary flex-grow ${level > 0 ? 'py-1' : 'py-4'}`}
            onClick={onClose}
            prefetch
          >
            {item.title}
          </Link>
          {hasChildren && (
            <button
              className="px-4 py-2 text-primary-studio"
              onClick={(e) => {
                e.preventDefault();
                toggleExpand(String(item.id));
              }}
            >
              <ArrowTwoIcon
                className={`transition-transform duration-200`}
                size="16px"
                fill={primaryColor}
                direction={`${isExpanded ? 'top' : 'bottom'}`}
              />
            </button>
          )}
        </div>
        {hasChildren && isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {subItems.map(subItem => renderMenuItem(subItem, level + 1))}
          </motion.div>
        )}
        <hr className={`dotted-spaced ${level !== 0 ? 'hidden' : ''}`} />
      </div>
    );
  };

  return (
    <AnimatePresence mode="wait" initial={false}>
      {isOpen && (
        <>
          <motion.div
            aria-hidden="true"
            onClick={onClose}
            className="fixed bottom-0 left-0 right-0 top-0 z-40 bg-[rgba(0,0,0,0.3)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { delay: 0.2 } }}
            transition={{ duration: 0.3 }}
          />
          <motion.div
            className={`${classes} fixed right-0 top-0 h-full w-[80%] sm:w-1/2 xl:w-[27%] z-[100] flex flex-col`}
            style={style}
            initial={{ opacity: 0, x: '100%' }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col h-full">
              <div className="py-3 px-4 relative text-primary-studio bg-primary-light-studio">
                <div className="text-center h-8 absolute top-2 left-2">
                  <button onClick={onClose} title={t('close')}>
                    <CloseOneIcon size="30px" stroke={primaryColor} />
                  </button>
                </div>
                <div className="flex">
                  {isLoggedIn ? (
                    <>
                      <Link href="/my-account" className="flex items-center hover:opacity-80 transition duration-300 w-1/2" onClick={onClose}>
                        <ProfileOneIcon width="24px" height="24px" className="me-2" fill={primaryColor} stroke="none" />
                        <span className="font-semibold text-center">{userData?.first_name} {userData?.last_name}</span>
                      </Link>
                      <Link href="/my-account?tab=my-orders" className="flex items-center hover:opacity-80 transition duration-300 w-1/2" onClick={onClose}>
                        <OrdersIcon width="24px" height="24px" className="me-2" fill={primaryColor} stroke="none" />
                        <span className="font-semibold">{accountT('my_orders')}</span>
                      </Link>
                    </>
                  ) : (
                    <Link href="/login" className="flex items-center hover:opacity-80 transition duration-300" onClick={onClose}>
                      <ProfileOneIcon width="24px" height="24px" className="me-2" fill={primaryColor} stroke="none" />
                      <span className="font-semibold">{accountT('login')}</span>
                    </Link>
                  )}
                </div>
                <hr className="dotted-spaced my-4" />
                <div className="flex">
                  {isLoggedIn ? (
                    <>
                      <Link href="/my-account?tab=my-orders" className="flex items-center hover:opacity-80 transition duration-300 w-1/2" onClick={onClose}>
                        <ReorderIcon width="24px" height="24px" className="me-2" fill={primaryColor} stroke="none" />
                        <span className="font-semibold text-center">{accountT('restore_last_order')}</span>
                      </Link>
                      {isFavoritesEnabled ? (
                        <Link
                          href="/my-account?tab=my-favorites"
                          className="flex items-center hover:opacity-80 transition duration-300 w-1/2"
                          onClick={onClose}
                        >
                          <HeartIcon width="24" height="24" className="me-2" fill={primaryColor} />
                          <span className="font-semibold">{accountT('my_favorites')}</span>
                        </Link>
                      ) : (
                        <button
                          className="flex items-center hover:opacity-80 transition duration-300 text-red-600"
                          onClick={handleLogoutClick}
                        >
                          <LogoutIcon width="24px" height="24px" className="me-2" fill="currentColor" stroke="none" />
                          <span className="font-semibold ">{accountT('logout')}</span>
                        </button>
                      )}
                    </>
                  ) : (
                    <Link href="/my-account?tab=my-orders" className="flex items-center hover:opacity-80 transition duration-300 w-1/2" onClick={onClose}>
                      <ReorderIcon width="24px" height="24px" className="me-2" fill={primaryColor} stroke="none" />
                      <span className="font-semibold text-center">{accountT('restore_last_order')}</span>
                    </Link>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto scrollbar px-2">
                <div className="menu-items-list">
                  {parentItems.map(item => renderMenuItem(item))}
                  {studioStore?.on_sale_page?.show_in_menu && (
                    <div className="mb-0">
                      <div className="flex items-center justify-between">
                        <Link
                          href="/on-sale"
                          className="block px-3 text-primary-studio font-secondary text-secondary-studio flex-grow py-4"
                          onClick={onClose}
                          prefetch
                        >
                          {studioStore?.on_sale_page?.menu_title}
                        </Link>
                      </div>
                      <hr className="dotted-spaced" />
                    </div>
                  )}
                </div>
              </div>
              <div className="px-4 text-white bg-primary-studio">
                {topMenuItems.map((item) => (
                  <div key={item.url} className="mb-0">
                    <Link
                      href={item.url}
                      className="block px-3 py-4 text-white"
                      onClick={onClose}
                    >
                      {item.title}
                    </Link>
                    <hr className="dotted-spaced-white" />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}));

MobileMenu.propTypes = {
  studioStore: PropTypes.shape({
    settings: PropTypes.shape({
      header: PropTypes.shape({
        main_menu: PropTypes.shape({
          background_color: PropTypes.string,
        }),
      }),
    }),
  }),
  wooCommerceStore: PropTypes.shape({
    cart: PropTypes.array,
  }),
  generalSettingsStore: PropTypes.object,
  topMenuItems: PropTypes.arrayOf(PropTypes.object),
  mainMenuItems: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

export default MobileMenu;